import { createRootRoute, createRoute, createRouter, redirect, useNavigate } from '@tanstack/react-router';
import { IconError404 } from '@tabler/icons-react';
import { CHARGE_STATES, WORKLIST_STATES, type CardType, type ChargeState, type WorklistState } from '@taksikortti/shared';
import { msalInstance } from './auth/AuthProvider';
import { EmptyState } from './components/EmptyState';
import { t } from './lib/strings';
import { Root } from './routes/Root';
import { DashboardPage } from './routes/DashboardPage';
import { WorklistPage } from './routes/WorklistPage';
import { PalvelutunnuksetPage } from './routes/PalvelutunnuksetPage';
import { CaseDetailPage } from './routes/CaseDetailPage';
import { DuplicateDetailPage } from './routes/DuplicateDetailPage';
import { ImportPage } from './routes/ImportPage';
import { KorttitoimitusPage } from './routes/KorttitoimitusPage';
import { AdminMigratePage } from './routes/AdminMigratePage';

const rootRoute = createRootRoute({ component: Root });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage,
});

const CARD_TYPE_VALUES: CardType[] = ['kertakortti', 'taksikortti', 'taksikortti_plus', 'tuntematon'];
type SortKey = 'cardType' | 'korttinumero' | 'asiakas' | 'charges' | 'state' | 'invoiceState' | 'paymentState' | 'createdAt' | 'firstAt' | 'lastAt';
const SORT_KEYS: SortKey[] = ['cardType', 'korttinumero', 'asiakas', 'charges', 'state', 'invoiceState', 'paymentState', 'createdAt', 'firstAt', 'lastAt'];

const asChargeStates = (v: unknown): ChargeState[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const r = v.filter((x): x is ChargeState => typeof x === 'string' && (CHARGE_STATES as readonly string[]).includes(x));
  return r.length ? r : undefined;
};

const asWorklistStates = (v: unknown): WorklistState[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const r = v.filter((x): x is WorklistState => typeof x === 'string' && (WORKLIST_STATES as readonly string[]).includes(x));
  return r.length ? r : undefined;
};

interface WorklistSearch {
  q?: string;
  cardType?: CardType[];
  kind?: 'all' | 'case' | 'duplicate';
  showDone?: boolean;
  state?: WorklistState[];
  invoiceState?: ChargeState[];
  paymentState?: ChargeState[];
  sortKey?: SortKey;
  sortDir?: 'asc' | 'desc';
  page?: number;
}

const casesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cases',
  component: WorklistPage,
  validateSearch: (s: Record<string, unknown>): WorklistSearch => ({
    q: typeof s.q === 'string' ? s.q : undefined,
    cardType: Array.isArray(s.cardType)
      ? s.cardType.filter((x): x is CardType => typeof x === 'string' && (CARD_TYPE_VALUES as string[]).includes(x))
      : undefined,
    kind: s.kind === 'case' || s.kind === 'duplicate' || s.kind === 'all' ? s.kind : undefined,
    showDone: s.showDone === true ? true : undefined,
    state: asWorklistStates(s.state),
    invoiceState: asChargeStates(s.invoiceState),
    paymentState: asChargeStates(s.paymentState),
    sortKey: typeof s.sortKey === 'string' && (SORT_KEYS as string[]).includes(s.sortKey) ? (s.sortKey as SortKey) : undefined,
    sortDir: s.sortDir === 'asc' || s.sortDir === 'desc' ? s.sortDir : undefined,
    page: typeof s.page === 'number' && s.page > 0 ? s.page : undefined,
  }),
});

interface PalvelutunnuksetSearch { q?: string; repeatOnly?: boolean }
const palvelutunnuksetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/palvelutunnukset',
  component: PalvelutunnuksetPage,
  validateSearch: (s: Record<string, unknown>): PalvelutunnuksetSearch => ({
    q: typeof s.q === 'string' ? s.q : undefined,
    repeatOnly: s.repeatOnly === false ? false : true,
  }),
});

const caseDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cases/$korttinumero/$caseId',
  component: CaseDetailPage,
});

const duplicateDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cases/tupla/$korttinumero/$groupId',
  component: DuplicateDetailPage,
});

// Old separate duplicates view — folded into the unified worklist.
const duplicatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/duplicates',
  beforeLoad: () => { throw redirect({ to: '/cases' }); },
});

const importRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/import',
  component: ImportPage,
});

const korttitoimitusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/korttitoimitus',
  component: KorttitoimitusPage,
});

const adminMigrateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/migrate',
  // Defense-in-depth client gate — the API still enforces the admin role.
  beforeLoad: () => {
    const claims = msalInstance.getActiveAccount()?.idTokenClaims as { roles?: string[] } | undefined;
    if (!claims?.roles?.includes('admin')) throw redirect({ to: '/' });
  },
  component: AdminMigratePage,
});

/** Friendly 404 rendered inside the shell. */
function NotFound() {
  const navigate = useNavigate();
  return (
    <EmptyState
      icon={<IconError404 size={30} />}
      title={t.app.notFoundTitle}
      hint={t.app.notFoundHint}
      action={{ label: t.app.notFoundCta, onClick: () => void navigate({ to: '/' }) }}
    />
  );
}

const routeTree = rootRoute.addChildren([
  indexRoute, casesRoute, palvelutunnuksetRoute, duplicateDetailRoute, caseDetailRoute,
  duplicatesRoute, importRoute, korttitoimitusRoute, adminMigrateRoute,
]);

export const router = createRouter({ routeTree, defaultNotFoundComponent: NotFound });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
